import { api, Application } from '@/lib/api';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Pagination,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEye, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

export default function ApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const response = await api.getApplications(page, 10);
      setApplications(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Не удалось загрузить заявки:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, [page]);

  const handleView = (application: Application) => {
    setSelectedApplication(application);
    open();
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Вы уверены, что хотите удалить эту заявку?')) return;

    try {
      await api.deleteApplication(id);
      loadApplications();
    } catch (error) {
      alert('Не удалось удалить заявку');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <Group justify="space-between" mb="xl">
        <Title order={2}>Заявки</Title>
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>Имя</Table.Th>
            <Table.Th>Телефон</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Город</Table.Th>
            <Table.Th>Какое изделие интересует</Table.Th>
            <Table.Th>ID продукта</Table.Th>
            <Table.Th>Создано</Table.Th>
            <Table.Th style={{ width: 120 }}>Действия</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading ? (
            <Table.Tr>
              <Table.Td colSpan={9} style={{ textAlign: 'center' }}>
                Загрузка...
              </Table.Td>
            </Table.Tr>
          ) : applications.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={9} style={{ textAlign: 'center' }}>
                Заявки не найдены
              </Table.Td>
            </Table.Tr>
          ) : (
            applications.map((application) => (
              <Table.Tr key={application.id}>
                <Table.Td>{application.id}</Table.Td>
                <Table.Td>{application.name}</Table.Td>
                <Table.Td>
                  {application.phone ? (
                    <Text
                      component="a"
                      href={`tel:${application.phone.replace(/\D/g, '')}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                        e.currentTarget.style.color = '#228be6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                        e.currentTarget.style.color = 'inherit';
                      }}
                    >
                      {application.phone}
                    </Text>
                  ) : (
                    <Text c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td>{application.email || <Text c="dimmed">-</Text>}</Table.Td>
                <Table.Td>{application.city || <Text c="dimmed">-</Text>}</Table.Td>
                <Table.Td>
                  {application.interestedProduct ? (
                    <Text size="sm" lineClamp={2}>{application.interestedProduct}</Text>
                  ) : (
                    <Text c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {application.productId ? (
                    <Badge color="blue">{application.productId}</Badge>
                  ) : (
                    <Text c="dimmed">-</Text>
                  )}
                </Table.Td>
                <Table.Td>{formatDate(application.createdAt)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      variant="light"
                      color="blue"
                      onClick={() => handleView(application)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="light"
                      color="red"
                      onClick={() => handleDelete(application.id)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))
          )}
        </Table.Tbody>
      </Table>

      {totalPages > 1 && (
        <Group justify="center" mt="xl">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}

      <Modal
        opened={opened}
        onClose={close}
        title="Детали заявки"
        size="lg"
      >
        {selectedApplication && (
          <div>
            <Group mb="md">
              <Text fw={600}>ID:</Text>
              <Text>{selectedApplication.id}</Text>
            </Group>
            <Group mb="md">
              <Text fw={600}>Имя:</Text>
              <Text>{selectedApplication.name}</Text>
            </Group>
            <Group mb="md">
              <Text fw={600}>Телефон:</Text>
              <Text>{selectedApplication.phone}</Text>
            </Group>
            <Group mb="md">
              <Text fw={600}>Email:</Text>
              {selectedApplication.email ? (
                <Text>{selectedApplication.email}</Text>
              ) : (
                <Text c="dimmed">Не указан</Text>
              )}
            </Group>
            <Group mb="md">
              <Text fw={600}>Город:</Text>
              {selectedApplication.city ? (
                <Text>{selectedApplication.city}</Text>
              ) : (
                <Text c="dimmed">Не указан</Text>
              )}
            </Group>
            {selectedApplication.interestedProduct && (
              <div style={{ marginBottom: '1rem' }}>
                <Text fw={600} mb="xs">Какое изделие интересует:</Text>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedApplication.interestedProduct}</Text>
              </div>
            )}
            {selectedApplication.description && (
              <div style={{ marginBottom: '1rem' }}>
                <Text fw={600} mb="xs">Описание:</Text>
                <Text style={{ whiteSpace: 'pre-wrap' }}>{selectedApplication.description}</Text>
              </div>
            )}
            {selectedApplication.productId && (
              <Group mb="md">
                <Text fw={600}>Product ID:</Text>
                <Badge color="blue">{selectedApplication.productId}</Badge>
              </Group>
            )}
            <Group mb="md">
              <Text fw={600}>Создано:</Text>
              <Text>{formatDate(selectedApplication.createdAt)}</Text>
            </Group>
            <Group justify="flex-end" mt="xl">
              <Button variant="default" onClick={close}>
                Закрыть
              </Button>
            </Group>
          </div>
        )}
      </Modal>
    </div>
  );
}
